import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound } from "lucide-react";

export default function MfaCard() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.mfa_enabled);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setEnabled(!!data.mfa_enabled)).catch(() => {});
  }, []);

  const startSetup = async () => {
    try {
      const { data } = await api.post("/auth/mfa/setup", { current_password: pw });
      setSecret(data.secret);
      setOtpauth(data.otpauth_url);
      setSetupOpen(true);
      setPwOpen(false);
      setPw("");
      setCode("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter 6-digit code"); return; }
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("MFA enabled");
      setEnabled(true);
      setSetupOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter 6-digit code"); return; }
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("MFA disabled");
      setEnabled(false);
      setDisableOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const qr = otpauth ? `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(otpauth)}&size=180x180&margin=10` : "";

  return (
    <div className="card-clinical p-5 flex items-start gap-4" data-testid="mfa-card">
      <div className={`h-11 w-11 rounded-xl grid place-items-center ${enabled ? "bg-vacant" : "bg-warn"}`}>
        {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
      </div>
      <div className="flex-1">
        <div className="font-medium">Two-factor authentication</div>
        <div className="text-xs text-muted-foreground mt-1">
          {enabled
            ? "Enabled — you will be asked for a 6-digit code on every sign-in."
            : "Not enabled. Recommended for admin accounts before clinical use."}
        </div>
        <div className="mt-3 flex gap-2">
          {!enabled ? (
            <Button size="sm" className="rounded-full" onClick={() => { setPw(""); setPwOpen(true); }} data-testid="mfa-setup"><KeyRound size={14} className="mr-2" /> Enable MFA</Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setCode(""); setDisableOpen(true); }} data-testid="mfa-disable">Disable MFA</Button>
          )}
        </div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm your password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">For security we re-verify your password before binding a new authenticator.</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" data-testid="mfa-pw" />
          <DialogFooter><Button className="rounded-full" onClick={startSetup}>Continue</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set up authenticator</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Scan the QR code with Google Authenticator / 1Password / Authy, then enter the 6-digit code shown.
            </p>
            {qr && <img src={qr} alt="MFA QR" className="rounded-lg border border-border mx-auto" width={180} height={180} />}
            <div>
              <div className="text-xs text-muted-foreground mb-1">Or enter this secret manually:</div>
              <div className="font-mono text-xs bg-muted rounded-md p-2 break-all">{secret}</div>
            </div>
            <Input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code}
                   onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                   className="tabular text-lg tracking-[0.4em] text-center" placeholder="123456" data-testid="mfa-code-input" />
          </div>
          <DialogFooter><Button className="rounded-full" onClick={verify} data-testid="mfa-verify">Verify & enable</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Disable MFA</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Enter your current 6-digit code to confirm.</p>
          <Input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                 className="tabular text-lg tracking-[0.4em] text-center" />
          <DialogFooter><Button variant="destructive" className="rounded-full" onClick={disable}>Disable</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
